import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { getVisitorIdFromCookie } from "./utils/visitor";
import GoogleAuthPanel from "./components/GoogleAuthPanel";
import { signinHrefWithRedirect, signupHrefWithRedirect } from "./utils/authRedirect";

export default function TranscriptPage({ isDarkMode, visitorId }) {
  const { youtubeId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const menuRef = useRef(null);
  const hamburgerRef = useRef(null);

  // ============ STATES ============
  const [transcript, setTranscript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [aiGenerationCount, setAiGenerationCount] = useState(0);
  const [aiGenerationLimit] = useState(1);

  const [aiLoading, setAiLoading] = useState(false);
  const [aiResults, setAiResults] = useState([]);
  const [aiError, setAiError] = useState(null);

  const [popupMessage, setPopupMessage] = useState(null);
  const [showAuthPopup, setShowAuthPopup] = useState(false);
  const [authPopupReason, setAuthPopupReason] = useState("");
  const [authPopupStep, setAuthPopupStep] = useState("offer");
  const [authPopupIntent, setAuthPopupIntent] = useState("ai");
  const [authModalError, setAuthModalError] = useState("");
  const [authTick, setAuthTick] = useState(0);
  const authIntentRef = useRef("ai");

  const [videoTitle, setVideoTitle] = useState("");
  const [channelName, setChannelName] = useState("");
  const [publishDate, setPublishDate] = useState("");
  const [videoLanguage, setVideoLanguage] = useState("en");
  const [videoViewCount, setVideoViewCount] = useState(1200000);
  const [transcriptLines, setTranscriptLines] = useState([]);
  const [showTimestamps, setShowTimestamps] = useState(true);

  const [showTranslateDropdown, setShowTranslateDropdown] = useState(false);
  const [rateLimitCooldown, setRateLimitCooldown] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);

  void authTick;
  const token = localStorage.getItem("token");
  const isLoggedIn = Boolean(token);

  useEffect(() => {
    const onAuth = () => setAuthTick((t) => t + 1);
    window.addEventListener("cliptranscript-auth-changed", onAuth);
    return () => window.removeEventListener("cliptranscript-auth-changed", onAuth);
  }, []);

  // ============ RESPONSIVE BREAKPOINTS ============
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const isDesktop = windowWidth >= 1024;

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'nl', name: 'Dutch' },
  ];

  // ============ COLORS ============
  const primaryBlue = "#007bff";
  const successGreen = "#28a745";
  const backgroundColor = isDarkMode ? "#1a1a1a" : "#ffffff";
  const cardBackground = isDarkMode ? "#2a2a2a" : "#ffffff";
  const textColor = isDarkMode ? "#e0e0e0" : "#333333";
  const secondaryText = isDarkMode ? "#b0b0b0" : "#666666";
  const borderColor = isDarkMode ? "#333333" : "#e0e0e0";
  const hoverBg = isDarkMode ? "#3a3a3a" : "#f5f5f5";

  // ============ RESPONSIVE PADDING ============
  const getPadding = () => {
    if (isMobile) return "clamp(12px, 4vw, 20px)";
    if (isTablet) return "clamp(16px, 3vw, 24px)";
    return "32px";
  };

  const getGap = () => {
    if (isMobile) return "clamp(8px, 3vw, 16px)";
    if (isTablet) return "clamp(12px, 2vw, 20px)";
    return "28px";
  };

  // ============ RESPONSIVE FONT SIZES ============
  const getTitleSize = () => {
    if (isMobile) return "clamp(18px, 6vw, 24px)";
    if (isTablet) return "clamp(20px, 4vw, 28px)";
    return "24px";
  };

  const getHeadingSize = () => {
    if (isMobile) return "clamp(14px, 4vw, 16px)";
    if (isTablet) return "clamp(15px, 3vw, 18px)";
    return "16px";
  };

  const thumbnailUrl = useMemo(() => {
    if (!youtubeId) return "";
    return `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
  }, [youtubeId]);

  // ============ WINDOW RESIZE LISTENER ============
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ============ CLOSE MOBILE MENU WITH ESC ============
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [mobileMenuOpen]);

  // ============ CLOSE MENU ON CLICK OUTSIDE ============
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        mobileMenuOpen &&
        menuRef.current &&
        hamburgerRef.current &&
        !menuRef.current.contains(e.target) &&
        !hamburgerRef.current.contains(e.target)
      ) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileMenuOpen]);

  // ============ CLOSE MENU WHEN LEAVING MOBILE VIEW ============
  useEffect(() => {
    if (!isMobile && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  }, [isMobile, mobileMenuOpen]);

  const getTranscriptRedirectPath = (withAutoSave = false) =>
    withAutoSave ? `/t/${youtubeId}?autosave=1` : `/t/${youtubeId}`;

  const goToSignup = (withAutoSave = false) => {
    navigate(`/signup?redirect=${encodeURIComponent(getTranscriptRedirectPath(withAutoSave))}`);
  };

  const openAuthPopup = (reason, intent = "ai") => {
    authIntentRef.current = intent;
    setAuthPopupIntent(intent);
    setAuthPopupReason(reason);
    setAuthPopupStep("offer");
    setAuthModalError("");
    setShowAuthPopup(true);
  };

  const handleAuthModalGoogleSuccess = useCallback(async () => {
    const intent = authIntentRef.current;
    authIntentRef.current = "ai";
    setAuthPopupIntent("ai");
    setShowAuthPopup(false);
    setAuthPopupStep("offer");

    const t = localStorage.getItem("token");
    if (intent === "save" && youtubeId && t && !isSaved) {
      setSavingStatus(true);
      try {
        const headers = { Authorization: `Bearer ${t}` };
        await axios.post(
          `/api/transcript-save/`,
          { youtube_id: youtubeId, is_saved: true },
          { headers }
        );
        setIsSaved(true);
        setPopupMessage("Transcript saved to your dashboard ✅");
      } catch (err) {
        console.error("Error saving after sign-in:", err);
        setPopupMessage("Signed in — tap Save again to keep this transcript.");
      } finally {
        setSavingStatus(false);
      }
    } else {
      setPopupMessage("You're signed in. You can save this transcript or use AI tools.");
    }
  }, [youtubeId, isSaved]);

  const toggleSaveTranscript = async () => {
    if (!youtubeId || !isLoggedIn) {
      openAuthPopup("Create an account to save this transcript immediately.", "save");
      return;
    }

    setSavingStatus(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`/api/transcript-save/`, {
        youtube_id: youtubeId,
        is_saved: !isSaved
      }, { headers });
      setIsSaved(!isSaved);
    } catch (err) {
      console.error("Error toggling save:", err);
    } finally {
      setSavingStatus(false);
    }
  };

  // ============ SEARCH FUNCTIONALITY ============
  const handleSearch = () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const query = searchQuery.toLowerCase();
    const results = transcriptLines
      .map((line, idx) => ({
        idx,
        text: line.text,
        timestamp: line.timestamp,
        match: line.text.toLowerCase().includes(query),
      }))
      .filter((item) => item.match);

    setSearchResults(results);
  };

  useEffect(() => {
    if (searchQuery.trim()) {
      handleSearch();
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, transcriptLines]);

  // ============ FETCH TRANSCRIPT DATA ============
  useEffect(() => {
    if (!youtubeId) {
      navigate("/");
      return;
    }

    const fetchTranscript = async () => {
      try {
        console.log(`🔄 Fetching transcript for video: ${youtubeId}`);

        const response = await axios.get(`/api/transcript-detail/${youtubeId}/`);

        console.log('📡 API Response:', response.data);

        if (response.data) {
          const title = response.data.title || "No Title Available";
          const channel = response.data.channel_name || "Unknown Channel";
          const pubDate = response.data.published_date || new Date().toLocaleDateString();

          setVideoTitle(title);
          setChannelName(channel);
          setPublishDate(pubDate);
          setVideoLanguage(response.data.language || "en");
          setVideoViewCount(response.data.view_count || 1200000);
          setTranscript(response.data);
          setIsSaved(response.data.is_saved || false);

          if (response.data.transcript_text) {
            const lines = response.data.transcript_text.split("\n").map((line) => {
              const match = line.match(/\[(\d{2}):(\d{2}):(\d{2})\]\s(.*)/);
              if (match) {
                const [, hours, minutes, seconds, text] = match;
                return {
                  timestamp: `${hours}:${minutes}:${seconds}`,
                  text: text,
                };
              }
              return { timestamp: null, text: line };
            });
            setTranscriptLines(lines);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("❌ Error fetching transcript:", err);
        setError(err.response?.data?.detail || "Transcript not found");
        setLoading(false);
      }
    };

    fetchTranscript();
  }, [youtubeId, navigate]);

  // ============ RATE LIMIT COOLDOWN ============
  useEffect(() => {
    if (rateLimitCooldown <= 0) return;

    const timer = setInterval(() => {
      setRateLimitCooldown((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [rateLimitCooldown]);

  useEffect(() => {
    const shouldAutoSave = searchParams.get("autosave") === "1";
    if (!shouldAutoSave || !isLoggedIn || !youtubeId || isSaved || savingStatus) return;

    const autoSaveTranscript = async () => {
      setSavingStatus(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        await axios.post(`/api/transcript-save/`, {
          youtube_id: youtubeId,
          is_saved: true
        }, { headers });
        setIsSaved(true);
        setPopupMessage("Transcript saved to your dashboard ✅");
      } catch (err) {
        console.error("Error auto-saving transcript:", err);
      } finally {
        setSavingStatus(false);
      }
    };

    autoSaveTranscript();
  }, [isLoggedIn, isSaved, savingStatus, youtubeId, searchParams, token]);

  // ============ AI ACTIONS ============
  const handleAIAction = async (action, extras = {}) => {
    if (!isLoggedIn) {
      openAuthPopup("AI generation is available only for registered users.");
      return;
    }

    if (rateLimitCooldown > 0) {
      setPopupMessage(`⏱️ Too many requests. Please wait ${rateLimitCooldown} seconds before trying again.`);
      return;
    }

    const localLimit = aiGenerationLimit;
    if (aiGenerationCount >= localLimit) {
      setPopupMessage("AI generation limit reached.");
      return;
    }

    setAiLoading(true);
    setAiError(null);

    try {
      const currentVisitorId = visitorId || getVisitorIdFromCookie();
      const payload = {
        visitor_id: currentVisitorId,
        action,
        youtube_id: youtubeId,
        ...extras,
      };

      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.post("/api/ai_action/", payload, { headers });

      if (response.data?.success) {
        const nextCount = aiGenerationCount + 1;
        setAiGenerationCount(nextCount);

        const resultType = action === "summary"
          ? "Summary"
          : action === "key_takeaways"
            ? "Key Takeaways"
            : action === "translate"
              ? `Translation - ${languages.find(l => l.code === extras.target_lang)?.name || "Translation"}`
              : action;

        const nextResult = {
          id: `${action}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
          action,
          type: resultType,
          content: response.data.content || "",
          timestamp: new Date().toLocaleString(),
        };

        setAiResults((prev) => [nextResult, ...prev]);

        const localLimit = aiGenerationLimit;
        if (nextCount >= localLimit) {
          setPopupMessage("AI generation limit reached.");
        }
      }
    } catch (err) {
      console.error('❌ AI action error:', err);

      if (err.response?.status === 429) {
        const retryAfter = parseInt(err.response?.headers?.['retry-after'] || '30', 10);
        setRateLimitCooldown(retryAfter);
        setPopupMessage(`⏱️ Too many requests. Server requests to wait ${retryAfter} seconds. Please try again later.`);
      }
      else if (err.response?.status === 500) {
        setPopupMessage("❌ Server error. Please try again in a moment.");
      } else if (err.message === 'Network Error') {
        setPopupMessage("❌ Network error. Please check your connection.");
      } else {
        setPopupMessage("❌ Error processing AI request. Please try again.");
      }
    } finally {
      setAiLoading(false);
    }
  };

  // ============ COPY TRANSCRIPT ============
  const buildTranscriptForCopy = () => {
    if (!transcriptLines?.length) return "";
    return transcriptLines
      .map((line) => {
        const tsPart = showTimestamps && line.timestamp ? `${line.timestamp} ` : "";
        return `${tsPart}${line.text}`.trim();
      })
      .join("\n");
  };

  const copyTranscript = async () => {
    const content = buildTranscriptForCopy();
    if (!content) {
      setPopupMessage("No transcript available to copy");
      return;
    }
    try {
      await navigator.clipboard.writeText(content);
      setPopupMessage("Transcript copied ✅");
    } catch (e) {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = content;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        setPopupMessage("Transcript copied ✅");
      } catch (err) {
        setPopupMessage("Copy failed. Please try again.");
      }
    }
  };

  // ============ DOWNLOAD TRANSCRIPT ============
  const downloadTranscript = (format) => {
    if (!transcript || !transcriptLines.length) {
      setPopupMessage("No transcript available to download");
      return;
    }
    let content = "";
    let filename = `${videoTitle || youtubeId}`;

    if (format === "txt") {
      content = transcriptLines
        .map((line) => {
          const tsPart = showTimestamps && line.timestamp ? `${line.timestamp} ` : "";
          return `${tsPart}${line.text}`.trim();
        })
        .join("\n");
      filename += ".txt";
    } else if (format === "srt") {
      content = transcriptLines
        .map((line, idx) => {
          if (!line.timestamp) return "";
          const timestamp = line.timestamp.replace(/:/g, ':') + ',000';
          return `${idx + 1}\n${timestamp} --> ${timestamp}\n${line.text}`;
        })
        .filter(Boolean)
        .join("\n\n");
      filename += ".srt";
    }

    const element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:text/plain;charset=utf-8," + encodeURIComponent(content)
    );
    element.setAttribute("download", filename);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // ============ LOADING STATE ============
  if (loading) {
    return (
      <div style={{
        padding: getPadding(),
        textAlign: "center",
        backgroundColor: isDarkMode ? "#1a1a1a" : "#f7fafd",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        color: textColor
      }}>
        <div style={{
          width: "clamp(40px, 15vw, 60px)",
          height: "clamp(40px, 15vw, 60px)",
          border: `4px solid ${borderColor}`,
          borderTop: `4px solid ${primaryBlue}`,
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
          marginBottom: "20px"
        }}></div>
        <div style={{ fontSize: "clamp(16px, 5vw, 24px)", fontWeight: "bold" }}>
          ⏳ Loading Transcript...
        </div>
      </div>
    );
  }

  // ============ ERROR STATE ============
  if (error) {
    return (
      <div style={{
        padding: getPadding(),
        textAlign: "center",
        color: "red",
        backgroundColor: isDarkMode ? "#1a1a1a" : "#f7fafd",
        minHeight: "calc(100vh - 180px)",
      }}>
        <div style={{ fontSize: "clamp(14px, 4vw, 18px)", marginBottom: "20px" }}>
          ❌ Error: {error}
        </div>
        <button
          onClick={() => navigate("/")}
          style={{
            padding: "clamp(8px, 2vw, 10px) clamp(14px, 3vw, 20px)",
            backgroundColor: primaryBlue,
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            fontSize: "clamp(12px, 3vw, 14px)",
            fontWeight: "600",
          }}
        >
          ← Back to Home
        </button>
      </div>
    );
  }

  const used = aiGenerationCount;
  const localLimit = aiGenerationLimit;
  const aiDisabled = used >= localLimit;

  // ============ MAIN RENDER ============
  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.6; }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          @keyframes slideDown {
            from {
              transform: translateY(-20px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          @keyframes hamburgerLine1 {
            0% { transform: rotate(0deg) translateY(0); }
            100% { transform: rotate(45deg) translateY(10px); }
          }
          @keyframes hamburgerLine2 {
            0% { opacity: 1; }
            100% { opacity: 0; }
          }
          @keyframes hamburgerLine3 {
            0% { transform: rotate(0deg) translateY(0); }
            100% { transform: rotate(-45deg) translateY(-10px); }
          }
          * { box-sizing: border-box; }
          
          /* Mobile Menu Animation */
          .mobile-menu-open .hamburger-line1 {
            animation: hamburgerLine1 0.3s ease forwards;
          }
          .mobile-menu-open .hamburger-line2 {
            animation: hamburgerLine2 0.3s ease forwards;
          }
          .mobile-menu-open .hamburger-line3 {
            animation: hamburgerLine3 0.3s ease forwards;
          }
          
          /* Scroll lock when menu is open */
          body.menu-open {
            overflow: hidden;
          }
        `}
      </style>

      {/* AI Loading Modal */}
      {aiLoading && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1001,
          backdropFilter: "blur(4px)",
          padding: getPadding(),
        }}>
          <div style={{
            backgroundColor: cardBackground,
            borderRadius: "16px",
            padding: "clamp(32px, 8vw, 48px)",
            textAlign: "center",
            boxShadow: isDarkMode ? "0 20px 60px rgba(0, 0, 0, 0.5)" : "0 20px 60px rgba(0, 0, 0, 0.15)",
            maxWidth: "400px",
            animation: "float 3s ease-in-out infinite",
            color: textColor,
            width: "100%",
          }}>
            <div style={{
              width: "clamp(60px, 15vw, 80px)",
              height: "clamp(60px, 15vw, 80px)",
              margin: "0 auto clamp(16px, 4vw, 24px)",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              <div style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                border: "3px solid transparent",
                borderTop: `3px solid ${primaryBlue}`,
                borderRight: `3px solid #28a745`,
                animation: "spin 2s linear infinite",
              }} />

              <div style={{
                position: "absolute",
                width: "75%",
                height: "75%",
                borderRadius: "50%",
                border: "2px solid transparent",
                borderBottom: `2px solid ${primaryBlue}`,
                borderLeft: "2px solid #f39c12",
                animation: "spin 1.5s linear infinite reverse",
              }} />

              <div style={{
                width: "clamp(10px, 3vw, 16px)",
                height: "clamp(10px, 3vw, 16px)",
                borderRadius: "50%",
                backgroundColor: primaryBlue,
                animation: "pulse 1.5s ease-in-out infinite",
              }} />
            </div>

            <h2 style={{
              margin: "0 0 clamp(8px, 2vw, 12px) 0",
              fontSize: "clamp(16px, 4vw, 20px)",
              fontWeight: "700",
              color: textColor,
              letterSpacing: "-0.5px",
            }}>
              🤖 AI is thinking...
            </h2>

            <p style={{
              margin: "0 0 clamp(16px, 4vw, 24px) 0",
              fontSize: "clamp(12px, 3vw, 14px)",
              color: secondaryText,
              lineHeight: "1.6",
            }}>
              Please wait while we prepare the best result for you
            </p>

            <div style={{
              display: "flex",
              justifyContent: "center",
              gap: "clamp(4px, 2vw, 8px)",
            }}>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    width: "clamp(6px, 1.5vw, 8px)",
                    height: "clamp(6px, 1.5vw, 8px)",
                    borderRadius: "50%",
                    backgroundColor: primaryBlue,
                    animation: `pulse 1.4s ease-in-out infinite`,
                    animationDelay: `${i * 0.2}s`,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Popup Modal */}
      {popupMessage && (
        <div style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: cardBackground,
          color: textColor,
          padding: "clamp(16px, 4vw, 24px)",
          borderRadius: "8px",
          boxShadow: isDarkMode ? "0 10px 40px rgba(0, 0, 0, 0.3)" : "0 10px 40px rgba(0, 0, 0, 0.15)",
          zIndex: 1000,
          maxWidth: "clamp(280px, 90vw, 400px)",
          textAlign: "center",
          border: `1px solid ${borderColor}`,
          animation: "slideDown 0.3s ease",
        }}>
          <p style={{
            margin: "0 0 clamp(12px, 3vw, 16px) 0",
            fontSize: "clamp(14px, 3vw, 16px)",
            fontWeight: 500
          }}>
            {popupMessage}
          </p>
          <button
            onClick={() => setPopupMessage(null)}
            style={{
              padding: "clamp(6px, 2vw, 8px) clamp(12px, 3vw, 16px)",
              backgroundColor: primaryBlue,
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              fontWeight: 500,
              fontSize: "clamp(12px, 2vw, 14px)",
              transition: "background-color 0.3s"
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = "#0056b3"}
            onMouseLeave={(e) => e.target.style.backgroundColor = primaryBlue}
          >
            OK
          </button>
        </div>
      )}

      {showAuthPopup && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1002,
          padding: getPadding(),
        }}>
          <div style={{
            backgroundColor: cardBackground,
            color: textColor,
            borderRadius: "12px",
            border: `1px solid ${borderColor}`,
            width: "100%",
            maxWidth: "430px",
            padding: "clamp(18px, 4vw, 24px)",
            boxShadow: isDarkMode ? "0 10px 40px rgba(0, 0, 0, 0.35)" : "0 10px 40px rgba(0, 0, 0, 0.2)",
          }}>
            {authPopupStep === "offer" && (
              <>
                <h3 style={{ margin: "0 0 10px 0", fontSize: "clamp(16px, 3vw, 20px)" }}>
                  Sign in to continue
                </h3>
                <p style={{
                  margin: "0 0 16px 0",
                  color: secondaryText,
                  lineHeight: "1.6",
                  fontSize: "clamp(13px, 2.5vw, 15px)",
                }}>
                  {authPopupReason}
                </p>
                <p style={{
                  margin: "0 0 14px 0",
                  color: secondaryText,
                  fontSize: "clamp(12px, 2.2vw, 14px)",
                  lineHeight: 1.5,
                }}>
                  Continue here so you stay on this transcript after signing in.
                </p>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setAuthModalError("");
                      setAuthPopupStep("signup");
                    }}
                    style={{
                      flex: "1 1 160px",
                      border: "none",
                      borderRadius: "8px",
                      padding: "10px 14px",
                      backgroundColor: successGreen,
                      color: "white",
                      cursor: "pointer",
                      fontWeight: "600",
                    }}
                  >
                    Create account
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAuthModalError("");
                      setAuthPopupStep("signin");
                    }}
                    style={{
                      flex: "1 1 120px",
                      border: `1px solid ${borderColor}`,
                      borderRadius: "8px",
                      padding: "10px 14px",
                      backgroundColor: "transparent",
                      color: textColor,
                      cursor: "pointer",
                      fontWeight: "600",
                    }}
                  >
                    Sign in
                  </button>
                </div>
                <p style={{
                  margin: "14px 0 0 0",
                  fontSize: "clamp(12px, 2vw, 13px)",
                  color: secondaryText,
                  textAlign: "center",
                }}>
                  Prefer the full page?{" "}
                  <Link
                    to={signupHrefWithRedirect(getTranscriptRedirectPath(authPopupIntent === "save"))}
                    style={{ color: primaryBlue, fontWeight: 600 }}
                  >
                    Sign up
                  </Link>
                  {" · "}
                  <Link
                    to={signinHrefWithRedirect(getTranscriptRedirectPath(authPopupIntent === "save"))}
                    style={{ color: primaryBlue, fontWeight: 600 }}
                  >
                    Sign in
                  </Link>
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setShowAuthPopup(false);
                    setAuthPopupStep("offer");
                  }}
                  style={{
                    width: "100%",
                    border: "none",
                    backgroundColor: "transparent",
                    color: secondaryText,
                    cursor: "pointer",
                    padding: "10px 0 0 0",
                  }}
                >
                  Maybe later
                </button>
              </>
            )}
            {(authPopupStep === "signin" || authPopupStep === "signup") && (
              <>
                <h3 style={{ margin: "0 0 10px 0", fontSize: "clamp(16px, 3vw, 20px)" }}>
                  {authPopupStep === "signup" ? "Create account" : "Sign in"}
                </h3>
                <p style={{
                  margin: "0 0 14px 0",
                  color: secondaryText,
                  lineHeight: "1.6",
                  fontSize: "clamp(13px, 2.5vw, 15px)",
                }}>
                  Use your Google account. You&apos;ll return to this transcript automatically.
                </p>
                <GoogleAuthPanel
                  buttonId={authPopupStep === "signup" ? "google-transcript-modal-signup" : "google-transcript-modal-signin"}
                  variant={authPopupStep === "signup" ? "signup_with" : "signin_with"}
                  isDarkMode={isDarkMode}
                  onSuccess={handleAuthModalGoogleSuccess}
                  onError={(msg) => setAuthModalError(msg)}
                />
                {authModalError && (
                  <p style={{ color: "#ef4444", margin: "10px 0 0 0", fontSize: "14px" }}>
                    {authModalError}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setAuthModalError("");
                    setAuthPopupStep("offer");
                  }}
                  style={{
                    width: "100%",
                    border: "none",
                    backgroundColor: "transparent",
                    color: secondaryText,
                    cursor: "pointer",
                    padding: "12px 0 0 0",
                    fontSize: "14px",
                  }}
                >
                  ← Back
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Background */}
      <div style={{
        backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
        minHeight: "100vh",
        padding: getPadding(),
        color: textColor,
      }}>
        {/* Main Container */}
        <div style={{
          maxWidth: "1200px",
          margin: "0 auto",
          backgroundColor: cardBackground,
          borderRadius: "clamp(6px, 1.5vw, 8px)",
          boxShadow: isDarkMode ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.1)",
          overflow: "hidden",
          border: `1px solid ${borderColor}`,
        }}>

          {/* Header Section */}
          <div style={{
            padding: `clamp(16px, 4vw, 28px) clamp(16px, 4vw, 32px)`,
            borderBottom: `1px solid ${borderColor}`,
            display: "flex",
            gap: getGap(),
            alignItems: isMobile ? "center" : "flex-start",
            flexDirection: isMobile ? "row" : "row",
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}>
            {thumbnailUrl && (
              <div
                style={{
                  position: "relative",
                  width: isMobile ? "clamp(100px, 30vw, 120px)" : "150px",
                  height: isMobile ? "clamp(60px, 18vw, 75px)" : "85px",
                  flexShrink: 0,
                  cursor: "pointer",
                  borderRadius: "5px",
                  overflow: "hidden",
                  minWidth: isMobile ? "100px" : "150px",
                }}
                onClick={() => window.open(`https://www.youtube.com/watch?v=${youtubeId}`, "_blank")}
              >
                <img
                  src={thumbnailUrl}
                  alt="Video thumbnail"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "5px",
                    transition: "transform 0.2s ease",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(0, 0, 0, 0.4)",
                    opacity: 0,
                    transition: "opacity 0.2s ease",
                  }}
                  onMouseEnter={(e) => (e.target.style.opacity = "1")}
                  onMouseLeave={(e) => (e.target.style.opacity = "0")}
                >
                  <div style={{
                    width: "clamp(30px, 10vw, 40px)",
                    height: "clamp(30px, 10vw, 40px)",
                    borderRadius: "50%",
                    backgroundColor: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "clamp(14px, 5vw, 20px)",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                  }}>
                    <span style={{ color: "#ff0000", marginLeft: "2px" }}>▶</span>
                  </div>
                </div>
              </div>
            )}

            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{
                margin: "0 0 clamp(8px, 2vw, 12px) 0",
                fontSize: getTitleSize(),
                fontWeight: "bold",
                color: textColor,
                lineHeight: "1.3",
                wordBreak: "break-word",
              }}>
                {videoTitle}
              </h1>
              <div style={{
                fontSize: "clamp(12px, 2.5vw, 14px)",
                color: secondaryText,
                display: "flex",
                gap: "clamp(6px, 2vw, 12px)",
                alignItems: "center",
                flexWrap: "wrap",
                lineHeight: "1.5",
              }}>
                <span style={{ whiteSpace: "nowrap" }}>🕒 {publishDate}</span>
                <span style={{ display: isMobile ? "none" : "inline" }}>•</span>
                <span style={{ whiteSpace: "nowrap" }}>
                  {channelName ? `Channel: ${channelName}` : "Channel: Unknown"}
                </span>
                <span style={{ display: isMobile ? "none" : "inline" }}>•</span>
                <span style={{ whiteSpace: "nowrap" }}>
                  {videoLanguage ? `${videoLanguage.toUpperCase()}` : "EN"}
                </span>
              </div>
            </div>
          </div>

          {/* Action Bar */}
          <div style={{
            margin: "0 clamp(16px, 4vw, 32px)",
            padding: "clamp(12px, 3vw, 16px) 0",
            borderBottom: `1px solid ${borderColor}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "clamp(8px, 2vw, 12px)",
          }}>
            <div style={{
              display: "flex",
              gap: "clamp(6px, 2vw, 12px)",
              flexWrap: "wrap",
              alignItems: "center",
              width: isMobile ? "100%" : "auto",
            }}>
              <button
                onClick={copyTranscript}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "clamp(4px, 1.5vw, 8px)",
                  padding: "clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 16px)",
                  backgroundColor: successGreen,
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  fontSize: "clamp(11px, 2.5vw, 14px)",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "background-color 0.3s",
                  minHeight: "clamp(32px, 8vw, 40px)",
                  justifyContent: "center",
                  flex: isMobile ? "1 1 auto" : "0 0 auto",
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = "#218838"}
                onMouseLeave={(e) => e.target.style.backgroundColor = successGreen}
              >
                <span style={{ fontSize: "clamp(12px, 3vw, 16px)" }}>📋</span>
                <span style={{ display: isMobile ? "none" : "inline" }}>Copy</span>
              </button>

              <button
                onClick={() => downloadTranscript("txt")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "clamp(4px, 1.5vw, 8px)",
                  padding: "clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 16px)",
                  backgroundColor: primaryBlue,
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  fontSize: "clamp(11px, 2.5vw, 14px)",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "background-color 0.3s",
                  minHeight: "clamp(32px, 8vw, 40px)",
                  justifyContent: "center",
                  flex: isMobile ? "1 1 auto" : "0 0 auto",
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = "#0056b3"}
                onMouseLeave={(e) => e.target.style.backgroundColor = primaryBlue}
              >
                <span style={{ fontSize: "clamp(12px, 3vw, 16px)" }}>📥</span>
                <span style={{ display: isMobile ? "none" : "inline" }}>TXT</span>
              </button>

              <button
                onClick={() => downloadTranscript("srt")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "clamp(4px, 1.5vw, 8px)",
                  padding: "clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 16px)",
                  backgroundColor: "transparent",
                  color: primaryBlue,
                  border: `2px solid ${primaryBlue}`,
                  borderRadius: "5px",
                  fontSize: "clamp(11px, 2.5vw, 14px)",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "background-color 0.3s",
                  minHeight: "clamp(32px, 8vw, 40px)",
                  justifyContent: "center",
                  flex: isMobile ? "1 1 auto" : "0 0 auto",
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = hoverBg}
                onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
              >
                <span style={{ fontSize: "clamp(12px, 3vw, 16px)" }}>📥</span>
                <span style={{ display: isMobile ? "none" : "inline" }}>SRT</span>
              </button>
            </div>

            <button
              onClick={() => setShowSearch(!showSearch)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "clamp(4px, 1.5vw, 6px)",
                padding: "clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 16px)",
                backgroundColor: isDarkMode ? "#333" : "#f0f0f0",
                color: secondaryText,
                border: "none",
                borderRadius: "5px",
                fontSize: "clamp(11px, 2.5vw, 14px)",
                fontWeight: "600",
                cursor: "pointer",
                transition: "background-color 0.3s",
                minHeight: "clamp(32px, 8vw, 40px)",
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = isDarkMode ? "#444" : "#e0e0e0"}
              onMouseLeave={(e) => e.target.style.backgroundColor = isDarkMode ? "#333" : "#f0f0f0"}
            >
              <span style={{ fontSize: "clamp(12px, 3vw, 16px)" }}>🔍</span>
              <span style={{ display: isMobile ? "none" : "inline" }}>Search</span>
            </button>
          </div>

          {/* Search Box */}
          {showSearch && (
            <div style={{
              margin: "0 clamp(16px, 4vw, 32px)",
              padding: "clamp(12px, 3vw, 16px) 0",
              borderBottom: `1px solid ${borderColor}`,
              display: "flex",
              gap: "clamp(6px, 2vw, 10px)",
              alignItems: "center",
              flexWrap: isMobile ? "wrap" : "nowrap",
            }}>
              <input
                type="text"
                placeholder="Search in transcript..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  flex: 1,
                  minWidth: isMobile ? "100%" : "200px",
                  padding: "clamp(6px, 2vw, 8px) clamp(8px, 2vw, 12px)",
                  border: `1px solid ${borderColor}`,
                  borderRadius: "5px",
                  fontSize: "clamp(12px, 2.5vw, 14px)",
                  backgroundColor: cardBackground,
                  color: textColor,
                  outline: "none",
                  transition: "border-color 0.3s",
                  minHeight: "clamp(32px, 8vw, 40px)",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = primaryBlue;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = borderColor;
                }}
              />
              <span style={{
                padding: "clamp(4px, 1.5vw, 6px) clamp(8px, 2vw, 12px)",
                backgroundColor: hoverBg,
                borderRadius: "5px",
                fontSize: "clamp(10px, 2vw, 12px)",
                color: secondaryText,
                fontWeight: "600",
                whiteSpace: "nowrap",
              }}>
                {searchResults.length} results
              </span>
            </div>
          )}

          {/* Content Grid - Responsive */}
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 250px" : "1fr 280px",
            gap: getGap(),
            padding: `clamp(16px, 4vw, 28px) clamp(16px, 4vw, 32px)`,
            alignItems: "start",
          }}>

            {/* Left Column: Transcript + AI Results */}
            <div>
              {/* AI Results */}
              {aiResults.length > 0 && (
                <div style={{ marginBottom: getGap() }}>
                  {aiResults.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        backgroundColor: cardBackground,
                        border: `1px solid ${borderColor}`,
                        borderRadius: "8px",
                        padding: "clamp(12px, 3vw, 20px)",
                        marginBottom: "clamp(8px, 2vw, 16px)",
                      }}
                    >
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "clamp(8px, 2vw, 14px)",
                        flexWrap: isMobile ? "wrap" : "nowrap",
                        gap: "clamp(6px, 2vw, 10px)",
                      }}>
                        <h3 style={{
                          margin: "0",
                          fontSize: "clamp(13px, 2.5vw, 15px)",
                          fontWeight: "bold",
                          color: textColor,
                        }}>
                          ✅ {item.type}
                        </h3>
                        <button
                          onClick={() => {
                            const blob = new Blob([item.content], { type: "text/plain;charset=utf-8" });
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `${videoTitle || youtubeId}_${item.type.replace(/\s+/g, "_")}.txt`;
                            a.click();
                            window.URL.revokeObjectURL(url);
                          }}
                          style={{
                            fontSize: "clamp(10px, 2vw, 12px)",
                            padding: "clamp(4px, 1.5vw, 6px) clamp(8px, 2vw, 12px)",
                            borderRadius: "5px",
                            border: "none",
                            backgroundColor: primaryBlue,
                            color: "white",
                            cursor: "pointer",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "3px",
                            transition: "background-color 0.3s",
                            minHeight: "clamp(28px, 7vw, 32px)",
                            whiteSpace: "nowrap",
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "#0056b3"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = primaryBlue}
                        >
                          <span style={{ fontSize: "clamp(10px, 2.5vw, 12px)" }}>📥</span>
                          <span style={{ display: isMobile ? "none" : "inline" }}>Download</span>
                        </button>
                      </div>
                      <div style={{
                        fontSize: "clamp(12px, 2.5vw, 14px)",
                        color: textColor,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        lineHeight: "1.7",
                      }}>
                        {item.content}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Transcript Section */}
              <div>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "clamp(8px, 2vw, 14px)",
                  flexWrap: isMobile ? "wrap" : "nowrap",
                  gap: "clamp(6px, 2vw, 8px)",
                }}>
                  <h2 style={{
                    margin: "0",
                    fontSize: getHeadingSize(),
                    fontWeight: "bold",
                    color: textColor,
                  }}>
                    Transcript
                  </h2>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "clamp(4px, 1.5vw, 8px)",
                    backgroundColor: isDarkMode ? "#333" : "#f0f0f0",
                    padding: "clamp(3px, 1vw, 4px) clamp(6px, 1.5vw, 8px)",
                    borderRadius: "5px",
                    border: `1px solid ${borderColor}`,
                    width: isMobile ? "100%" : "fit-content",
                    justifyContent: isMobile ? "space-between" : "flex-start",
                  }}>
                    <span style={{
                      fontSize: "clamp(10px, 2vw, 12px)",
                      fontWeight: "600",
                      color: textColor,
                      whiteSpace: "nowrap",
                    }}>
                      Timestamps
                    </span>
                    <button
                      onClick={() => setShowTimestamps(!showTimestamps)}
                      style={{
                        position: "relative",
                        width: "clamp(30px, 8vw, 36px)",
                        height: "clamp(16px, 4vw, 20px)",
                        backgroundColor: showTimestamps ? primaryBlue : "#ccc",
                        border: "none",
                        borderRadius: "10px",
                        cursor: "pointer",
                        padding: "0",
                        transition: "background-color 0.3s ease",
                        boxShadow: isDarkMode ? "inset 0 1px 2px rgba(0,0,0,0.3)" : "inset 0 1px 2px rgba(0,0,0,0.1)",
                        flexShrink: 0,
                        minHeight: "clamp(16px, 4vw, 20px)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = showTimestamps ? "#0056b3" : "#bbb";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = showTimestamps ? primaryBlue : "#ccc";
                      }}
                    >
                      <div style={{
                        position: "absolute",
                        width: "clamp(12px, 3vw, 16px)",
                        height: "clamp(12px, 3vw, 16px)",
                        backgroundColor: "white",
                        borderRadius: "50%",
                        top: "clamp(2px, 0.5vw, 2px)",
                        left: showTimestamps ? "calc(100% - clamp(14px, 3.5vw, 18px))" : "clamp(2px, 0.5vw, 2px)",
                        transition: "left 0.3s ease",
                        boxShadow: "0 2px 3px rgba(0,0,0,0.2)",
                      }} />
                    </button>
                  </div>
                </div>

                <div style={{
                  backgroundColor: cardBackground,
                  border: `1px solid ${borderColor}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                }}>
                  {searchResults.length > 0 && searchQuery ? (
                    <div style={{
                      maxHeight: isMobile ? "400px" : "600px",
                      overflowY: "auto",
                    }}>
                      {searchResults.map((result, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "clamp(6px, 2vw, 12px)",
                            padding: "clamp(6px, 2vw, 8px) clamp(8px, 2vw, 12px)",
                            borderBottom: idx < searchResults.length - 1 ? `1px solid ${borderColor}` : "none",
                            fontSize: "clamp(13px, 2.5vw, 15px)",
                            color: textColor,
                            lineHeight: "1.6",
                            backgroundColor: idx % 2 === 0 ? "transparent" : hoverBg,
                          }}
                        >
                          {result.timestamp ? (
                            <span style={{
                              color: primaryBlue,
                              fontWeight: "bold",
                              fontSize: "clamp(11px, 2vw, 13px)",
                              flexShrink: 0,
                              minWidth: "clamp(40px, 10vw, 50px)",
                            }}>
                              {result.timestamp}
                            </span>
                          ) : null}
                          <span>{result.text}</span>
                        </div>
                      ))}
                    </div>
                  ) : transcriptLines.length > 0 ? (
                    showTimestamps ? (
                      <div style={{
                        maxHeight: isMobile ? "400px" : "600px",
                        overflowY: "auto",
                      }}>
                        {transcriptLines.map((line, idx) => (
                          <div
                            key={idx}
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "clamp(6px, 2vw, 12px)",
                              padding: "clamp(6px, 2vw, 8px) clamp(8px, 2vw, 12px)",
                              borderBottom: idx < transcriptLines.length - 1 ? `1px solid ${borderColor}` : "none",
                              fontSize: "clamp(13px, 2.5vw, 15px)",
                              color: textColor,
                              lineHeight: "1.6",
                              backgroundColor: idx % 2 === 0 ? "transparent" : hoverBg,
                            }}
                          >
                            {line.timestamp ? (
                              <span style={{
                                color: primaryBlue,
                                fontWeight: "bold",
                                fontSize: "clamp(11px, 2vw, 13px)",
                                flexShrink: 0,
                                minWidth: "clamp(40px, 10vw, 50px)",
                              }}>
                                {line.timestamp}
                              </span>
                            ) : null}
                            <span>{line.text}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{
                        padding: "clamp(10px, 3vw, 12px) clamp(12px, 3vw, 18px)",
                        fontSize: "clamp(13px, 2.5vw, 15px)",
                        color: textColor,
                        lineHeight: "1.8",
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                      }}>
                        {transcriptLines.map((line) => line.text).join(" ")}
                      </div>
                    )
                  ) : (
                    <div style={{
                      padding: "clamp(20px, 5vw, 32px)",
                      textAlign: "center",
                      color: secondaryText,
                      fontSize: "clamp(12px, 2.5vw, 14px)",
                    }}>
                      No transcript available
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column: Sidebar - Mobile Hidden, Tablet/Desktop Visible */}
            {!isMobile && (
              <div style={{
                display: "flex",
                flexDirection: "column",
                gap: "clamp(8px, 2vw, 12px)",
                position: "sticky",
                top: "20px",
              }}>

                {/* AI Tools */}
                <div style={{
                  backgroundColor: isDarkMode ? "#2a2a2a" : "#ffffff",
                  border: `1.5px solid ${isDarkMode ? "#333" : "#e3e6e8"}`,
                  borderRadius: "10px",
                  padding: "clamp(14px, 3vw, 20px)",
                }}>
                  <h3 style={{
                    margin: "0 0 clamp(3px, 1vw, 6px) 0",
                    fontSize: "clamp(13px, 2vw, 15px)",
                    fontWeight: "700",
                    color: isDarkMode ? "#e0e0e0" : "#21242b",
                  }}>
                    🤖 AI Tools
                  </h3>
                  <p style={{
                    margin: "0 0 clamp(4px, 1vw, 8px) 0",
                    fontSize: "clamp(11px, 1.8vw, 13px)",
                    color: isDarkMode ? "#b0b0b0" : "#848b99",
                  }}>
                    Enhance your transcript
                  </p>
                  {aiError && (
                    <p style={{
                      margin: "0 0 clamp(4px, 1vw, 8px) 0",
                      fontSize: "clamp(11px, 1.8vw, 13px)",
                      color: "#e74c3c",
                    }}>
                      {aiError}
                    </p>
                  )}

                  {/* Summary Button */}
                  <button
                    onClick={() => handleAIAction("summary")}
                    disabled={aiDisabled || aiLoading || rateLimitCooldown > 0}
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "clamp(8px, 2vw, 12px) clamp(10px, 2vw, 14px)",
                      marginBottom: "clamp(6px, 1.5vw, 10px)",
                      backgroundColor: isDarkMode ? "#333" : "#f8fafc",
                      border: `1.5px solid ${isDarkMode ? "#444" : "#f1f2f4"}`,
                      borderRadius: "8px",
                      color: isDarkMode ? "#64b5f6" : "#2363eb",
                      cursor: aiDisabled || aiLoading || rateLimitCooldown > 0 ? "not-allowed" : "pointer",
                      opacity: aiDisabled || aiLoading || rateLimitCooldown > 0 ? 0.6 : 1,
                      fontSize: "clamp(12px, 2vw, 14px)",
                      fontWeight: "600",
                      transition: "all 0.2s",
                      minHeight: "clamp(36px, 8vw, 44px)",
                    }}
                    onMouseEnter={(e) => {
                      if (!aiDisabled && !aiLoading && rateLimitCooldown === 0) {
                        e.currentTarget.style.backgroundColor = isDarkMode ? "#3a3a3a" : "#f0f6ff";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = isDarkMode ? "#333" : "#f8fafc";
                    }}
                    title={rateLimitCooldown > 0 ? `Wait ${rateLimitCooldown}s` : ""}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: "clamp(4px, 1.5vw, 8px)" }}>
                      <span style={{ fontSize: "clamp(14px, 3vw, 16px)" }}>✨</span> Summary
                    </span>
                    <span style={{
                      fontSize: "clamp(10px, 1.8vw, 12px)",
                      color: isDarkMode ? "#999" : "#8e98a4",
                      whiteSpace: "nowrap",
                    }}>
                      Key Insights
                    </span>
                  </button>

                  {/* Chapters Button */}
                  <button
                    onClick={() => handleAIAction("key_takeaways")}
                    disabled={aiDisabled || aiLoading || rateLimitCooldown > 0}
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "clamp(8px, 2vw, 12px) clamp(10px, 2vw, 14px)",
                      marginBottom: "clamp(6px, 1.5vw, 10px)",
                      backgroundColor: isDarkMode ? "#333" : "#f8fafc",
                      border: `1.5px solid ${isDarkMode ? "#444" : "#f1f2f4"}`,
                      borderRadius: "8px",
                      color: isDarkMode ? "#64b5f6" : "#2363eb",
                      cursor: aiDisabled || aiLoading || rateLimitCooldown > 0 ? "not-allowed" : "pointer",
                      opacity: aiDisabled || aiLoading || rateLimitCooldown > 0 ? 0.6 : 1,
                      fontSize: "clamp(12px, 2vw, 14px)",
                      fontWeight: "600",
                      transition: "all 0.2s",
                      minHeight: "clamp(36px, 8vw, 44px)",
                    }}
                    onMouseEnter={(e) => {
                      if (!aiDisabled && !aiLoading && rateLimitCooldown === 0) {
                        e.currentTarget.style.backgroundColor = isDarkMode ? "#3a3a3a" : "#f0f6ff";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = isDarkMode ? "#333" : "#f8fafc";
                    }}
                    title={rateLimitCooldown > 0 ? `Wait ${rateLimitCooldown}s` : ""}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: "clamp(4px, 1.5vw, 8px)" }}>
                      <span style={{ fontSize: "clamp(14px, 3vw, 16px)" }}>📑</span> Chapters
                    </span>
                    <span style={{
                      fontSize: "clamp(10px, 1.8vw, 12px)",
                      color: isDarkMode ? "#999" : "#8e98a4",
                      whiteSpace: "nowrap",
                    }}>
                      Auto-generate
                    </span>
                  </button>

                  {/* Translate Button */}
                  <div style={{ position: "relative" }}>
                    <button
                      onClick={() => setShowTranslateDropdown(!showTranslateDropdown)}
                      disabled={aiDisabled || aiLoading || rateLimitCooldown > 0}
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "clamp(8px, 2vw, 12px) clamp(10px, 2vw, 14px)",
                        backgroundColor: isDarkMode ? "#333" : "#f8fafc",
                        border: `1.5px solid ${isDarkMode ? "#444" : "#f1f2f4"}`,
                        borderRadius: "8px",
                        color: isDarkMode ? "#64b5f6" : "#2363eb",
                        cursor: aiDisabled || aiLoading || rateLimitCooldown > 0 ? "not-allowed" : "pointer",
                        opacity: aiDisabled || aiLoading || rateLimitCooldown > 0 ? 0.6 : 1,
                        fontSize: "clamp(12px, 2vw, 14px)",
                        fontWeight: "600",
                        transition: "all 0.2s",
                        minHeight: "clamp(36px, 8vw, 44px)",
                      }}
                      onMouseEnter={(e) => {
                        if (!aiDisabled && !aiLoading && rateLimitCooldown === 0) {
                          e.currentTarget.style.backgroundColor = isDarkMode ? "#3a3a3a" : "#f0f6ff";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = isDarkMode ? "#333" : "#f8fafc";
                      }}
                      title={rateLimitCooldown > 0 ? `Wait ${rateLimitCooldown}s` : ""}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: "clamp(4px, 1.5vw, 8px)" }}>
                        <span style={{ fontSize: "clamp(14px, 3vw, 16px)" }}>🌐</span> Translate
                      </span>
                      <span style={{
                        fontSize: "clamp(10px, 1.8vw, 12px)",
                        color: isDarkMode ? "#999" : "#8e98a4",
                        whiteSpace: "nowrap",
                      }}>
                        125+ Languages
                      </span>
                    </button>

                    {showTranslateDropdown && (
                      <div style={{
                        position: "absolute",
                        top: "100%",
                        left: "0",
                        right: "0",
                        backgroundColor: isDarkMode ? "#2a2a2a" : "#ffffff",
                        border: `1.5px solid ${isDarkMode ? "#444" : "#e3e6e8"}`,
                        borderRadius: "8px",
                        zIndex: 20,
                        marginTop: "6px",
                        maxHeight: "220px",
                        overflowY: "auto",
                        boxShadow: isDarkMode ? "0 8px 24px rgba(0, 0, 0, 0.3)" : "0 8px 24px rgba(0, 0, 0, 0.08)",
                      }}>
                        {languages.map((lang, idx) => (
                          <button
                            key={lang.code}
                            onClick={() => {
                              handleAIAction("translate", { target_lang: lang.code });
                              setShowTranslateDropdown(false);
                            }}
                            style={{
                              display: "block",
                              width: "100%",
                              padding: "clamp(7px, 2vw, 10px) clamp(10px, 2vw, 14px)",
                              backgroundColor: "transparent",
                              color: isDarkMode ? "#e0e0e0" : "#21242b",
                              border: "none",
                              textAlign: "left",
                              cursor: "pointer",
                              fontSize: "clamp(11px, 2vw, 13px)",
                              fontWeight: "500",
                              transition: "background-color 0.15s",
                              borderBottom: idx < languages.length - 1 ? `1px solid ${isDarkMode ? "#333" : "#f2f2f2"}` : "none",
                              minHeight: "clamp(32px, 7vw, 40px)",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = isDarkMode ? "#333" : "#f8fafc";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "transparent";
                            }}
                          >
                            {lang.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Save Transcript */}
                {!isLoggedIn ? (
                  <div style={{
                    backgroundColor: cardBackground,
                    border: `1px solid ${borderColor}`,
                    borderRadius: "8px",
                    padding: "clamp(14px, 3vw, 20px)",
                    textAlign: "center",
                  }}>
                    <h4 style={{
                      margin: "0 0 clamp(6px, 1.5vw, 8px) 0",
                      fontSize: "clamp(12px, 2vw, 14px)",
                      fontWeight: "bold",
                      color: textColor,
                    }}>
                      💾 Save this transcript?
                    </h4>
                    <p style={{
                      margin: "0 0 clamp(8px, 2vw, 14px) 0",
                      fontSize: "clamp(11px, 1.8vw, 13px)",
                      color: secondaryText,
                      lineHeight: "1.5",
                    }}>
                      Create an account to save and access your transcripts later.
                    </p>
                    <button
                      onClick={() => goToSignup(true)}
                      style={{
                        width: "100%",
                        padding: "clamp(8px, 2vw, 10px) clamp(10px, 2vw, 14px)",
                        backgroundColor: successGreen,
                        color: "white",
                        border: "none",
                        borderRadius: "5px",
                        cursor: "pointer",
                        fontSize: "clamp(12px, 2vw, 14px)",
                        fontWeight: "600",
                        transition: "background-color 0.3s",
                        minHeight: "clamp(36px, 8vw, 40px)",
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = "#1e7e34"}
                      onMouseLeave={(e) => e.target.style.backgroundColor = successGreen}
                    >
                      Create account
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={toggleSaveTranscript}
                    disabled={savingStatus}
                    style={{
                      backgroundColor: isSaved ? "#f39c12" : isDarkMode ? "#2a2a2a" : "#f8f9fa",
                      border: isSaved ? "none" : `1px solid ${borderColor}`,
                      padding: "clamp(12px, 3vw, 16px)",
                      cursor: savingStatus ? "not-allowed" : "pointer",
                      textAlign: "center",
                      transition: "all 0.3s",
                      borderRadius: "8px",
                      opacity: savingStatus ? 0.6 : 1,
                      minHeight: "clamp(100px, 20vw, 120px)",
                    }}
                    onMouseEnter={(e) => {
                      if (!savingStatus) {
                        e.target.style.opacity = "0.8";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!savingStatus) {
                        e.target.style.opacity = "1";
                      }
                    }}
                  >
                    <div style={{
                      fontSize: "clamp(24px, 6vw, 28px)",
                      marginBottom: "clamp(4px, 1.5vw, 8px)",
                    }}>
                      💾
                    </div>
                    <div style={{
                      fontSize: "clamp(12px, 2vw, 14px)",
                      fontWeight: "bold",
                      color: isSaved ? "white" : textColor,
                    }}>
                      {savingStatus ? "..." : isSaved ? "Saved" : "Save to Dashboard"}
                    </div>
                  </button>
                )}
              </div>
            )}

            {/* Mobile Sidebar - Bottom Section */}
            {isMobile && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "clamp(8px, 2vw, 12px)",
                marginTop: getGap(),
              }}>
                {/* AI Tools Mobile - Compact */}
                <div style={{
                  backgroundColor: isDarkMode ? "#2a2a2a" : "#ffffff",
                  border: `1.5px solid ${isDarkMode ? "#333" : "#e3e6e8"}`,
                  borderRadius: "10px",
                  padding: "clamp(10px, 2vw, 14px)",
                  gridColumn: "1 / -1",
                }}>
                  <h3 style={{
                    margin: "0 0 clamp(6px, 1.5vw, 8px) 0",
                    fontSize: "clamp(13px, 2.5vw, 15px)",
                    fontWeight: "700",
                    color: isDarkMode ? "#e0e0e0" : "#21242b",
                  }}>
                    🤖 AI Tools
                  </h3>
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "clamp(6px, 1.5vw, 8px)",
                  }}>
                    <button
                      onClick={() => handleAIAction("summary")}
                      disabled={aiDisabled || aiLoading || rateLimitCooldown > 0}
                      style={{
                        padding: "clamp(8px, 2vw, 10px)",
                        backgroundColor: isDarkMode ? "#333" : "#f8fafc",
                        border: `1.5px solid ${isDarkMode ? "#444" : "#f1f2f4"}`,
                        borderRadius: "6px",
                        color: isDarkMode ? "#64b5f6" : "#2363eb",
                        cursor: aiDisabled || aiLoading || rateLimitCooldown > 0 ? "not-allowed" : "pointer",
                        opacity: aiDisabled || aiLoading || rateLimitCooldown > 0 ? 0.6 : 1,
                        fontSize: "clamp(10px, 2vw, 12px)",
                        fontWeight: "600",
                        transition: "all 0.2s",
                        minHeight: "clamp(40px, 10vw, 48px)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "2px",
                      }}
                      onMouseEnter={(e) => {
                        if (!aiDisabled && !aiLoading && rateLimitCooldown === 0) {
                          e.currentTarget.style.backgroundColor = isDarkMode ? "#3a3a3a" : "#f0f6ff";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = isDarkMode ? "#333" : "#f8fafc";
                      }}
                    >
                      <span style={{ fontSize: "clamp(14px, 3vw, 16px)" }}>✨</span>
                      <span>Summary</span>
                    </button>

                    <button
                      onClick={() => handleAIAction("key_takeaways")}
                      disabled={aiDisabled || aiLoading || rateLimitCooldown > 0}
                      style={{
                        padding: "clamp(8px, 2vw, 10px)",
                        backgroundColor: isDarkMode ? "#333" : "#f8fafc",
                        border: `1.5px solid ${isDarkMode ? "#444" : "#f1f2f4"}`,
                        borderRadius: "6px",
                        color: isDarkMode ? "#64b5f6" : "#2363eb",
                        cursor: aiDisabled || aiLoading || rateLimitCooldown > 0 ? "not-allowed" : "pointer",
                        opacity: aiDisabled || aiLoading || rateLimitCooldown > 0 ? 0.6 : 1,
                        fontSize: "clamp(10px, 2vw, 12px)",
                        fontWeight: "600",
                        transition: "all 0.2s",
                        minHeight: "clamp(40px, 10vw, 48px)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "2px",
                      }}
                      onMouseEnter={(e) => {
                        if (!aiDisabled && !aiLoading && rateLimitCooldown === 0) {
                          e.currentTarget.style.backgroundColor = isDarkMode ? "#3a3a3a" : "#f0f6ff";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = isDarkMode ? "#333" : "#f8fafc";
                      }}
                    >
                      <span style={{ fontSize: "clamp(14px, 3vw, 16px)" }}>📑</span>
                      <span>Chapters</span>
                    </button>

                    <button
                      onClick={() => setShowTranslateDropdown(!showTranslateDropdown)}
                      disabled={aiDisabled || aiLoading || rateLimitCooldown > 0}
                      style={{
                        gridColumn: "1 / -1",
                        padding: "clamp(8px, 2vw, 10px)",
                        backgroundColor: isDarkMode ? "#333" : "#f8fafc",
                        border: `1.5px solid ${isDarkMode ? "#444" : "#f1f2f4"}`,
                        borderRadius: "6px",
                        color: isDarkMode ? "#64b5f6" : "#2363eb",
                        cursor: aiDisabled || aiLoading || rateLimitCooldown > 0 ? "not-allowed" : "pointer",
                        opacity: aiDisabled || aiLoading || rateLimitCooldown > 0 ? 0.6 : 1,
                        fontSize: "clamp(10px, 2vw, 12px)",
                        fontWeight: "600",
                        transition: "all 0.2s",
                        minHeight: "clamp(36px, 9vw, 44px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                      onMouseEnter={(e) => {
                        if (!aiDisabled && !aiLoading && rateLimitCooldown === 0) {
                          e.currentTarget.style.backgroundColor = isDarkMode ? "#3a3a3a" : "#f0f6ff";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = isDarkMode ? "#333" : "#f8fafc";
                      }}
                    >
                      <span style={{ fontSize: "clamp(14px, 3vw, 16px)" }}>🌐</span>
                      <span>Translate</span>
                    </button>

                    {showTranslateDropdown && (
                      <div style={{
                        gridColumn: "1 / -1",
                        backgroundColor: isDarkMode ? "#2a2a2a" : "#ffffff",
                        border: `1.5px solid ${isDarkMode ? "#444" : "#e3e6e8"}`,
                        borderRadius: "8px",
                        zIndex: 20,
                        marginTop: "6px",
                        maxHeight: "200px",
                        overflowY: "auto",
                        boxShadow: isDarkMode ? "0 8px 24px rgba(0, 0, 0, 0.3)" : "0 8px 24px rgba(0, 0, 0, 0.08)",
                      }}>
                        {languages.map((lang, idx) => (
                          <button
                            key={lang.code}
                            onClick={() => {
                              handleAIAction("translate", { target_lang: lang.code });
                              setShowTranslateDropdown(false);
                            }}
                            style={{
                              display: "block",
                              width: "100%",
                              padding: "clamp(8px, 2vw, 10px) clamp(10px, 2vw, 14px)",
                              backgroundColor: "transparent",
                              color: isDarkMode ? "#e0e0e0" : "#21242b",
                              border: "none",
                              textAlign: "left",
                              cursor: "pointer",
                              fontSize: "clamp(11px, 2vw, 13px)",
                              fontWeight: "500",
                              transition: "background-color 0.15s",
                              borderBottom: idx < languages.length - 1 ? `1px solid ${isDarkMode ? "#333" : "#f2f2f2"}` : "none",
                              minHeight: "clamp(32px, 8vw, 40px)",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = isDarkMode ? "#333" : "#f8fafc";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "transparent";
                            }}
                          >
                            {lang.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Save Button Mobile */}
                {!isLoggedIn ? (
                  <button
                    onClick={() => goToSignup(true)}
                    style={{
                      gridColumn: "1 / -1",
                      backgroundColor: successGreen,
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      padding: "clamp(10px, 2.5vw, 14px)",
                      cursor: "pointer",
                      fontSize: "clamp(12px, 2.5vw, 14px)",
                      fontWeight: "600",
                      transition: "background-color 0.3s",
                      minHeight: "clamp(40px, 10vw, 48px)",
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = "#1e7e34"}
                    onMouseLeave={(e) => e.target.style.backgroundColor = successGreen}
                  >
                    Create account
                  </button>
                ) : (
                  <button
                    onClick={toggleSaveTranscript}
                    disabled={savingStatus}
                    style={{
                      gridColumn: "1 / -1",
                      backgroundColor: isSaved ? "#f39c12" : isDarkMode ? "#2a2a2a" : "#f8f9fa",
                      border: isSaved ? "none" : `1px solid ${borderColor}`,
                      padding: "clamp(10px, 2.5vw, 14px)",
                      cursor: savingStatus ? "not-allowed" : "pointer",
                      textAlign: "center",
                      transition: "all 0.3s",
                      borderRadius: "8px",
                      opacity: savingStatus ? 0.6 : 1,
                      fontSize: "clamp(12px, 2.5vw, 14px)",
                      fontWeight: "bold",
                      color: isSaved ? "white" : textColor,
                      minHeight: "clamp(40px, 10vw, 48px)",
                    }}
                    onMouseEnter={(e) => {
                      if (!savingStatus) {
                        e.target.style.opacity = "0.8";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!savingStatus) {
                        e.target.style.opacity = "1";
                      }
                    }}
                  >
                    <span style={{ fontSize: "clamp(14px, 3vw, 16px)" }}>💾</span>
                    {" "}{savingStatus ? "..." : isSaved ? "Saved" : "Save"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}